import React, { useState, useEffect, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { PostCreator } from './PostCreator';
import { useAuth } from '../contexts/AuthContext';
import { Community, UserData, Attachment, PostType, Post, ShareTarget, Achievement, DailyAchievements } from '../types';
import { sendChannelMessage, startConversationOnServer, addMessageToConversationOnServer } from '../api';
import { stickerTextStyle } from './StickerText';
import { CloseIcon, PlusIcon, SendIcon } from './Icons';
import { FootprintArchiveModal } from './FootprintPage';
import { getWeightedUserList } from '../utils/interactionUtils';

interface GlobalPostModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreatePost: (data: any) => any;
    communities: Community[];
    conversations: any[];
    postBlurOpacity: number;
    achievements: Achievement[];
    dailyAchievements: DailyAchievements;
    initialMedia?: string;
    initialDestination?: 'feed' | 'community' | 'footprint' | 'chats';
    initialSelectedTaskId?: number | null;
    initialSharedContent?: { text: string; media: any[]; sourcePostId?: number } | null;
}

export const GlobalPostModal: React.FC<GlobalPostModalProps> = ({
    isOpen,
    onClose,
    onCreatePost,
    communities,
    conversations,
    postBlurOpacity,
    achievements,
    dailyAchievements,
    initialMedia,
    initialDestination,
    initialSelectedTaskId,
    initialSharedContent
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser || ''];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const [destination, setDestination] = useState<'feed' | 'community' | 'footprint' | 'chats'>(initialDestination || 'feed');
    const [selectedCommunityId, setSelectedCommunityId] = useState<string>('');
    const [selectedChannelId, setSelectedChannelId] = useState<string>('posts');
    const [shareToUsers, setShareToUsers] = useState<string[]>([]);
    
    // We can allow searching users
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedArchivedTaskId, setSelectedArchivedTaskId] = useState<number | null>(null);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isCustomizingPost, setIsCustomizingPost] = useState(false);

    const dailyTask = dailyAchievements?.daily;
    const archivedTasks = achievements.filter(ach => ach.isArchived && (!dailyTask || ach.id !== dailyTask.id));

    useEffect(() => {
        if (isOpen) {
            setDestination(initialDestination || 'feed');
            setSelectedCommunityId('');
            setSelectedChannelId('');
            setShareToUsers([]);
            setSearchQuery('');
            // If an initialSelectedTaskId was provided, preselect it, otherwise reset
            setSelectedArchivedTaskId(initialSelectedTaskId ?? null);
            setIsCustomizingPost(false);
        }
    }, [isOpen, initialDestination, initialSelectedTaskId]);

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };
    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const joinedCommunities = communities.filter(c => c.members?.includes(currentUser || '') || c.admins?.includes(currentUser || '') || c.creator === currentUser);

    // Set default channel when community is selected
    useEffect(() => {
        if (selectedCommunityId && destination === 'community') {
            const selectedCommunity = joinedCommunities.find(c => c._id === selectedCommunityId);
            const postChannels = selectedCommunity?.channels?.filter((ch: any) => ch.type === 'posts') || [];
            if (postChannels.length > 0) {
                setSelectedChannelId(postChannels[0].id);
            } else {
                setSelectedChannelId('');
            }
        } else if (destination !== 'community') {
            setSelectedChannelId('');
        }
    }, [selectedCommunityId, destination, joinedCommunities]);

    const availableUsers = useMemo(() => {
        if (!currentUser) return [];
        const friends = currentUserData?.friends || [];
        const following = currentUserData?.following || [];
        const followers = currentUserData?.followers || [];
        const allRelatedUserIds = Array.from(new Set([...friends, ...following, ...followers]));
        
        const sortedIds = getWeightedUserList(
            currentUser,
            allRelatedUserIds,
            usersData,
            conversations || [],
            communities || [],
            []
        );

        return sortedIds.map(userId => ({
            id: userId,
            ...usersData[userId]
        })).filter(u => {
            if (!u) return false;
            const query = (searchQuery || '').toLowerCase();
            const nameMatch = u.displayName ? u.displayName.toLowerCase().includes(query) : false;
            const idMatch = u.id ? String(u.id).toLowerCase().includes(query) : false;
            return nameMatch || idMatch;
        });
    }, [currentUser, currentUserData, usersData, conversations, communities, searchQuery]);

    const handleCreatePost = async (postData: any) => {
        if (!currentUser) return;

        // DESTINATION: CHATS (Do NOT create a feed post - send directly as chat message)
        if (destination === 'chats') {
            if (shareToUsers.length === 0) {
                alert('Please select at least one chat, direct message, or channel recipient.');
                return;
            }
            try {
                for (const targetId of shareToUsers) {
                    if (targetId.startsWith('yipyap_')) {
                        const convoId = Number(targetId.replace('yipyap_', ''));
                        await addMessageToConversationOnServer(convoId, {
                            authorId: currentUser,
                            text: postData.content?.text || '',
                            media: postData.content?.media || []
                        });
                    } else {
                        const existing = conversations.find(c => !c.isYipYap && c.participants.includes(targetId));
                        let resolvedConvoId = existing?.id;
                        if (!resolvedConvoId) {
                            const conversation = await startConversationOnServer([currentUser, targetId]);
                            resolvedConvoId = conversation.id;
                        }
                        if (resolvedConvoId) {
                            await addMessageToConversationOnServer(resolvedConvoId, {
                                authorId: currentUser,
                                text: postData.content?.text || '',
                                media: postData.content?.media || []
                            });
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to send chat message", err);
            }
            onClose();
            return;
        }

        let finalData = { ...postData };
        if (destination === 'community') {
            if (!selectedCommunityId) {
                alert('Please select a community');
                return;
            }
            finalData.communityId = selectedCommunityId;
            if (selectedChannelId) {
                finalData.channelId = selectedChannelId;
            }
        } else if (destination === 'footprint') {
            const targetTaskId = selectedArchivedTaskId || dailyTask?.id;
            const completedTasks = currentUserData?.completedAchievements || [];
            const isAlreadyCompleted = targetTaskId && completedTasks.some((c: any) => Number(c.achievementId) === Number(targetTaskId));
            if (isAlreadyCompleted) {
                alert('You have already completed this footprint task!');
                return;
            }

            finalData.isFootprintPost = true;
            if (selectedArchivedTaskId) {
                const ach = archivedTasks.find(t => t.id === selectedArchivedTaskId);
                finalData.footprintDetails = {
                    achievementId: selectedArchivedTaskId,
                    achievementTitle: ach?.title || '',
                    achievementDifficulty: ach?.difficulty || 'Easy',
                    isTimely: false
                };
            } else if (dailyTask) {
                finalData.footprintDetails = {
                    achievementId: dailyTask.id,
                    achievementTitle: dailyTask.title,
                    achievementDifficulty: dailyTask.difficulty || 'Medium',
                    isTimely: true
                };
            }
        }

        const newPost = await onCreatePost(finalData);

        // Share post card via Direct Message if DM recipients were toggled
        if (newPost && shareToUsers.length > 0) {
            try {
                for (const targetId of shareToUsers) {
                    if (targetId.startsWith('yipyap_')) {
                        const convoId = Number(targetId.replace('yipyap_', ''));
                        const firstProfileBgSlide = currentUserData?.profileBgSlideshow?.enabled && currentUserData.profileBgSlideshow.attachments?.length > 0
                            ? currentUserData.profileBgSlideshow.attachments[0].url
                            : currentUserData?.profileBgImage?.url || currentUserData?.profileBg?.url;

                        await addMessageToConversationOnServer(convoId, {
                            authorId: currentUser,
                            text: '',
                            media: [],
                            sharedPostId: newPost.id,
                            sharedPostBgUrl: newPost.type === 'text' ? firstProfileBgSlide : undefined
                        });
                    } else {
                        const existing = conversations.find(c => !c.isYipYap && c.participants.includes(targetId));
                        let resolvedConvoId = existing?.id;
                        if (!resolvedConvoId) {
                            const conversation = await startConversationOnServer([currentUser, targetId]);
                            resolvedConvoId = conversation.id;
                        }
                        if (resolvedConvoId) {
                            const firstProfileBgSlide = currentUserData?.profileBgSlideshow?.enabled && currentUserData.profileBgSlideshow.attachments?.length > 0
                                ? currentUserData.profileBgSlideshow.attachments[0].url
                                : currentUserData?.profileBgImage?.url || currentUserData?.profileBg?.url;

                            await addMessageToConversationOnServer(resolvedConvoId, {
                                authorId: currentUser,
                                text: '',
                                media: [],
                                sharedPostId: newPost.id,
                                sharedPostBgUrl: newPost.type === 'text' ? firstProfileBgSlide : undefined
                            });
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to share post to users", err);
            }
        }
        
        onClose();
        return newPost;
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 backdrop-blur-lg z-[150] flex items-center justify-center animate-fade-in p-0 md:p-8 messages-page-no-swipe"
            style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
            onClick={onClose}
        >
            <div 
                className="relative w-full h-full md:h-[85vh] max-h-full md:max-w-4xl lg:max-w-6xl xl:max-w-7xl flex flex-col overflow-hidden shadow-2xl md:rounded-[2.5rem]"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex flex-col h-full w-full overflow-hidden">
                <div 
                    className="p-4 md:p-6 flex justify-between items-center shrink-0"
                    style={{ paddingTop: Capacitor.isNativePlatform() ? 'calc(1.5rem + env(safe-area-inset-top, 0px))' : '1rem' }}
                >
                    <h2 className="text-xl font-bold" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Create Post</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                        {/* Left Column: Destination, Community/Footprint options & PostComposer */}
                        <div className="md:col-span-7 flex flex-col gap-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Destination</label>
                                <div className="flex gap-2 items-center">
                                    {['feed', 'community', 'footprint', 'chats'].map(dest => (
                                        <button
                                            key={dest}
                                            onClick={() => setDestination(dest as any)}
                                            className={`px-4 py-2 rounded-full text-sm font-bold capitalize transition-colors border ${destination === dest ? 'bg-white/20 border-white/40 shadow-inner' : 'bg-black/20 border-transparent hover:bg-black/30'}`}
                                            style={stickerTextStyle(0.18, userTextColor)}
                                        >
                                            {dest}
                                        </button>
                                    ))}
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.dispatchEvent(new CustomEvent('app.openLinkModal'));
                                        }}
                                        className="p-1 hover:bg-white/10 rounded-full transition-all active:scale-90 flex-shrink-0 flex items-center justify-center text-3xl"
                                        style={{
                                            ...stickerTextStyle(0.18),
                                            filter: `drop-shadow(0 0 8px ${userThemeColor})`
                                        }}
                                        title="Link? Ask your friends to meet up"
                                    >
                                        📍
                                    </button>
                                </div>
                            </div>

                            {destination === 'community' && (
                                <div className="flex flex-col gap-2 animate-fade-in">
                                    <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Select Community</label>
                                    {joinedCommunities.length === 0 ? (
                                        <p className="text-xs opacity-70">You are not in any communities yet.</p>
                                    ) : (
                                        <select 
                                            className="bg-black/30 border border-white/20 rounded-2xl p-3 text-sm focus:outline-none focus:border-white/40"
                                            value={selectedCommunityId}
                                            onChange={(e) => {
                                                setSelectedCommunityId(e.target.value);
                                                // Reset channel selection when community changes
                                                setSelectedChannelId('posts');
                                            }}
                                        >
                                            <option value="">Choose...</option>
                                            {joinedCommunities.map(c => (
                                                <option key={c._id} value={c._id}>{c.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}

                            {destination === 'community' && selectedCommunityId && (
                                <div className="flex flex-col gap-2 animate-fade-in">
                                    <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Select Channel</label>
                                    {(() => {
                                        const comm = joinedCommunities.find(c => c._id === selectedCommunityId);
                                        const channels = comm?.channels?.filter((ch: any) => ch.type === 'posts') || [];
                                        if (channels.length === 0) return <p className="text-xs opacity-70">No post channels available in this community.</p>;
                                        return (
                                            <select 
                                                className="bg-black/30 border border-white/20 rounded-2xl p-3 text-sm focus:outline-none focus:border-white/40"
                                                value={selectedChannelId}
                                                onChange={(e) => setSelectedChannelId(e.target.value)}
                                            >
                                                {channels.map((ch: any) => (
                                                    <option key={ch.id} value={ch.id}>#{ch.name}</option>
                                                ))}
                                            </select>
                                        );
                                    })()}
                                </div>
                            )}

                            {destination === 'footprint' && (
                                <div className="flex flex-col gap-3 animate-fade-in">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Selected Task</label>
                                        {selectedArchivedTaskId && (
                                            <button
                                                onClick={() => setSelectedArchivedTaskId(null)}
                                                className="text-xs font-bold text-yellow-400 hover:text-yellow-300 transition-colors"
                                                style={stickerTextStyle(0.18)}
                                            >
                                                Return to Daily Task
                                            </button>
                                        )}
                                    </div>
                                    {(() => {
                                        const activeTask = selectedArchivedTaskId ? archivedTasks.find(t => t.id === selectedArchivedTaskId) : dailyTask;
                                        const isDaily = !selectedArchivedTaskId && !!dailyTask;
                                        if (!activeTask) return <div className="text-xs opacity-70 p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                                            {selectedArchivedTaskId ? 'Selected task not found.' : 'No daily task active. Browse archived tasks below to select one.'}
                                        </div>;

                                        const completed = currentUserData?.completedAchievements || [];
                                        const isAlreadyDone = completed.some((c: any) => Number(c.achievementId) === Number(activeTask.id));

                                        return (
                                            <div className={`w-full flex flex-col gap-2 p-3 rounded-xl border shadow-inner ${isAlreadyDone ? 'bg-red-500/10 border-red-500/30' : 'bg-blue-600/25 border-blue-500/50'}`}>
                                                <div className="flex items-center justify-between gap-4">
                                                    <div className="flex-grow min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-white text-base truncate" style={stickerTextStyle(0.15)}>{activeTask.title}</span>
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${isDaily ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`} style={stickerTextStyle(0.18)}>
                                                                {isDaily ? 'DAILY TASK' : 'ARCHIVED TASK'}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-white/70 mt-1 line-clamp-2" style={stickerTextStyle(0.18)}>{activeTask.description}</div>
                                                    </div>
                                                    <div className="flex-shrink-0 text-sm font-bold text-blue-400">
                                                        +{isDaily ? activeTask.points : Math.round(activeTask.points * 0.2)} pts
                                                    </div>
                                                </div>
                                                {isAlreadyDone && (
                                                    <div className="text-[11px] font-bold text-red-400 bg-red-500/20 border border-red-500/30 rounded-lg px-2.5 py-1 text-center">
                                                        ⚠️ You have already completed this task!
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    <button
                                        onClick={() => setIsArchiveModalOpen(true)}
                                        className="w-full py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-sm font-bold"
                                        style={stickerTextStyle(0.18, userTextColor)}
                                    >
                                        Browse Archived Tasks
                                    </button>
                                </div>
                            )}

                            <div className="w-full h-px bg-white/10 my-1" />

                            <div className="w-full">
                                <PostCreator
                                    onCreatePost={handleCreatePost}
                                    postBlurOpacity={postBlurOpacity}
                                    themeColor={userThemeColor}
                                    isFootprintPost={destination === 'footprint'}
                                    isCommunityPost={destination === 'community'}
                                    communityId={selectedCommunityId}
                                    channelId={selectedChannelId}
                                    onCancel={onClose}
                                    initialSharedContent={initialSharedContent || (initialMedia ? { text: '', media: [{ type: 'image', url: initialMedia, name: 'art.png', size: 0, file: null as any }] } : null)}
                                    hideLinkRequest={true}
                                    isChatsDestination={destination === 'chats'}
                                    onInternalModalStateChange={(isOpen) => setIsCustomizingPost(isOpen)}
                                    onOpenCustomizationModal={() => setIsCustomizingPost(true)}
                                />
                            </div>
                        </div>

                        {/* Right Column: Direct Message Sharing & Collapsible Weighted Recipients */}
                        <div className="md:col-span-5 flex flex-col gap-4">
                            <div className="flex flex-col gap-4">
                                <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>
                                    {destination === 'chats' ? 'Select Recipients (Required)' : 'Share via Direct Message (Optional)'}
                                </label>
                                <input
                                    type="text"
                                    placeholder="Search friends, followers, communities..."
                                    className="bg-black/30 border border-white/10 rounded-2xl p-2.5 text-sm focus:outline-none focus:border-white/40"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />

                                <div className="flex flex-col gap-4 overflow-y-auto max-h-[70vh] custom-scrollbar allow-scroll overscroll-contain" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
                                    {/* Section 1: Direct Messages */}
                                    <details open className="group">
                                        <summary className="w-full flex items-center justify-between mb-3 cursor-pointer select-none">
                                            <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                                💬 Direct Messages ({availableUsers.length})
                                            </h3>
                                            <span className="text-[10px] opacity-60 group-open:rotate-180 transition-transform">▼</span>
                                        </summary>
                                        <div className="flex flex-wrap gap-2 animate-fade-in">
                                            {availableUsers.map(u => {
                                                const isSelected = shareToUsers.includes(u.id);
                                                return (
                                                    <button
                                                        key={u.id}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setShareToUsers(prev => prev.filter(id => id !== u.id));
                                                            } else {
                                                                setShareToUsers(prev => [...prev, u.id]);
                                                            }
                                                        }}
                                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                                    >
                                                        {u.pfp ? (
                                                            <img src={u.pfp} className="w-5 h-5 rounded-full object-cover" alt="" />
                                                        ) : (
                                                            <div className="w-5 h-5 rounded-full bg-gray-500 flex items-center justify-center text-[10px] font-bold">
                                                                {String(u.id || '?')[0]?.toUpperCase()}
                                                            </div>
                                                        )}
                                                        <span className="truncate max-w-[100px] text-white font-medium">{u.displayName || u.id}</span>
                                                    </button>
                                                );
                                            })}
                                            {availableUsers.length === 0 && <span className="text-xs opacity-50 italic ml-1">No matches found.</span>}
                                        </div>
                                    </details>

                                    {/* Section 2: Community Chats */}
                                    <details open className="group">
                                        <summary className="w-full flex items-center justify-between mb-3 cursor-pointer select-none">
                                            <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                                🏰 Community Chats ({joinedCommunities.length})
                                            </h3>
                                            <span className="text-[10px] opacity-60 group-open:rotate-180 transition-transform">▼</span>
                                        </summary>
                                        <div className="flex flex-col gap-2 animate-fade-in">
                                            {joinedCommunities.map(comm => {
                                                const chatChannels = comm.channels?.filter((ch: any) => ch.type === 'chat') || [];
                                                return (
                                                    <div key={comm._id} className="flex flex-col gap-1 text-xs">
                                                        <div className="font-bold opacity-80 flex items-center gap-1.5 ml-1">
                                                            {comm.avatar && <img src={comm.avatar} className="w-4 h-4 rounded-full object-cover" alt="" />}
                                                            <span>{comm.name}</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1.5 pl-5">
                                                            {chatChannels.map((ch: any) => (
                                                                <button
                                                                    key={ch.id}
                                                                    onClick={() => {
                                                                        setDestination('community');
                                                                        setSelectedCommunityId(comm._id);
                                                                        setSelectedChannelId(ch.id);
                                                                    }}
                                                                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${destination === 'community' && selectedCommunityId === comm._id && selectedChannelId === ch.id ? 'bg-blue-500/30 border-blue-400 text-white' : 'bg-black/30 border-white/10 hover:bg-white/10 opacity-80'}`}
                                                                >
                                                                    #{ch.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {joinedCommunities.length === 0 && <span className="text-xs opacity-50 italic ml-1">No community chats joined.</span>}
                                        </div>
                                    </details>

                                    {/* Section 3: YipYap Channels */}
                                    <details open className="group">
                                        <summary className="w-full flex items-center justify-between mb-3 cursor-pointer select-none">
                                            <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                                🗣️ YipYap Channels ({conversations.filter(c => c.isYipYap && c.participants?.includes(currentUser)).length})
                                            </h3>
                                            <span className="text-[10px] opacity-60 group-open:rotate-180 transition-transform">▼</span>
                                        </summary>
                                        <div className="flex flex-wrap gap-2 animate-fade-in">
                                            {conversations.filter(c => c.isYipYap && c.participants?.includes(currentUser)).map(yy => {
                                                const isSelected = shareToUsers.includes(`yipyap_${yy.id}`);
                                                return (
                                                    <button
                                                        key={yy.id}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setShareToUsers(prev => prev.filter(id => id !== `yipyap_${yy.id}`));
                                                            } else {
                                                                setShareToUsers(prev => [...prev, `yipyap_${yy.id}`]);
                                                            }
                                                        }}
                                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                                    >
                                                        <div className="w-5 h-5 rounded-full bg-indigo-600/40 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                                                            🗣️
                                                        </div>
                                                        <span className="truncate max-w-[120px] text-white font-medium">{yy.yipYapName || yy.name || yy.groupName || `YipYap #${yy.id}`}</span>
                                                    </button>
                                                );
                                            })}
                                            {conversations.filter(c => c.isYipYap && c.participants?.includes(currentUser)).length === 0 && <span className="text-xs opacity-50 italic ml-1">No active YipYap channels.</span>}
                                        </div>
                                    </details>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </div>

            <FootprintArchiveModal
                isOpen={isArchiveModalOpen}
                onClose={() => setIsArchiveModalOpen(false)}
                dailyAchievements={dailyAchievements}
                onNavigateToAchievement={(id) => {
                    setSelectedArchivedTaskId(id);
                    setIsArchiveModalOpen(false);
                }}
            />
        </div>
    );
};
